import React, { useEffect, useRef, useState } from "react";
import {
  buildLaneSegment,
  translateSegment,
  projectShading,
  TRIPWIRE_HANDLE_RADIUS_PX,
  isProvisionalTripwireId,
} from "../../utils/tripwireUtils";
import { getTripwireColor } from "../../utils/tripwireColors";
import { editorCursor } from "../../utils/editorCursor";

// Fixed SCREEN-pixel depth for the shaded "restricted side" gradient (spec
// 2026-09-22(c): "fixed pixel depth regardless of zoom"). projectShading
// converts it to metres via the viewport's pxPerMeter, which includes the
// editor's zoom, so it stays this many screen pixels at any zoom.
const SHADING_DEPTH_PX = 32;
// Shading is drawn UNDER the line and is NOT part of the grab target: the
// line's own <line> stroke (and its endpoint/lane handles) already covers
// the whole clickable/draggable surface this tool needs (level-0 body drag,
// level-1 endpoint drag), and the shaded region can extend across other
// lines/stations the operator still needs to reach. pointerEvents="none"
// keeps it purely decorative.

// SVG overlay for the 越線 (tripwire) editor tool. Mirrors AreaPolygonEditor's
// drag pattern (native mousedown/mousemove/mouseup on window, not
// react-draggable) so endpoint/body dragging behaves identically to the
// walkable-area handles the operator already knows.
//
// Two placement modes, both documented in tripwireUtils.js:
//   - plane: two raw clicks anywhere on the canvas -> an arbitrary segment.
//   - line/tunnel: one click picks a position along the lane (the x axis);
//     the tripwire spans the full lane at that x (buildLaneSegment). Only x
//     is draggable in that mode -- there is no second, independently
//     draggable endpoint.
//
// Interaction levels (2026-09-22(b) UX revision), mirroring
// DraggableStation/StationPopover's drag+click+popover pattern:
//   - Level 0: every line is draggable as a whole (translate both points /
//     slide the lane gate), click-vs-drag disambiguated by a small pixel
//     threshold, exactly like DraggableStation's underlying react-draggable
//     onStop.
//   - Level 1: a focused line (focusedId) shows its endpoint handles and is
//     described by a popover the CALLER renders (TripwirePopover, anchored
//     via tripwireMidpointPx) -- this component only reports focus via
//     onSelect/onBackgroundClick, it does not render the popover itself.
//
// Persistence is NOT per-mousemove (unlike the walkable-area polygon, which
// only saves when the whole modal is saved): a tripwire round-trips its own
// REST endpoint immediately (useTripwires), so this component tracks a drag
// locally and only calls the *DragEnd callback once, on mouseup, with the
// final points -- one PUT per drag, not one per pixel. useTripwires.update
// commits that dropped position OPTIMISTICALLY (before the PUT resolves),
// so the line never visibly reverts to its pre-drag spot and then jumps
// forward once the request completes; on a 400 it reverts to the pre-drag
// position and shows message.error. Each *DragEnd callback is invoked
// BEFORE this component clears its own local drag override (see each
// onUp below) so there is no intermediate frame where neither the local
// override nor the (not-yet-optimistically-updated) parent state hold the
// dropped position.
const HANDLE_FILL = "#ffffff";
const BODY_DRAG_THRESHOLD_PX = 4;

const TripwireOverlay = ({
  layoutType,
  tripwires,
  canvasSize,
  dimensions,
  viewport, // this layout's own viewport (utils/viewport.js): every px <-> metre conversion goes through it
  focusedId,
  onSelect, // (id) => void -- a plain click (below the drag threshold) focuses a line
  onBackgroundClick, // () => void -- click on empty canvas while something is focused
  onEndpointDragEnd, // (tripwireId, endpointIndex, [mx, my]) -- plane only
  onBodyDragEnd, // (tripwireId, points) -- level-0 whole-line drag, both layouts
  onLaneDragEnd, // (tripwireId, newXMeters) -- line/tunnel handle drag only
  drawingPoints, // plane only: 0-2 pending meter points for a new line (add mode)
  previewPoint, // plane only: rubber-band endpoint following the mouse after point 1
  onDragActiveChange, // (boolean) => void -- fires on every drag-capable pointerdown/up,
  // so the caller (level-1 popover) can hide itself for the duration of a
  // handle/body drag and reappear on drop (addendum: a midpoint-anchored
  // popover must never sit there mid-drag fighting the operator's cursor).
  editable = true, // false in the editor's 瀏覽: lines are drawn but cannot be
  // focused, dragged or reshaped, and a press falls through to the canvas pan.
}) => {
  const [drag, setDrag] = useState(null); // { id, index, point } transient endpoint/lane override
  const [bodyDrag, setBodyDrag] = useState(null); // { id, points } transient whole-line override

  // The three drag helpers below attach mousemove/mouseup to `window`
  // imperatively (not via React's synthetic event system) and only remove
  // them from their own `onUp`. That is not enough on its own: each of the
  // three canvases (plane/line/tunnel) mounts its own TripwireOverlay, so
  // switching the active layout tab -- or closing the modal -- mid-drag
  // unmounts this component while the listeners are still live on `window`.
  // Track whichever pair is currently attached and remove it on unmount too,
  // so a drag that never reaches mouseup can never leak a listener or fire a
  // stale callback (closed-over `tripwire`/`dimensions`/`canvasSize` from an
  // unmounted render) later.
  const activeDragListenersRef = useRef(null);

  useEffect(() => {
    return () => {
      if (activeDragListenersRef.current) {
        const { onMove, onUp } = activeDragListenersRef.current;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        activeDragListenersRef.current = null;
      }
    };
  }, []);

  if (!viewport?.isValid) return null;
  // 瀏覽 lines inherit the canvas's grab: a press there pans.
  const cursorMode = editable ? "edit" : "browse";

  const svgStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: canvasSize.width,
    height: canvasSize.height,
    pointerEvents: "none",
    overflow: "visible",
  };

  // Provisional lines (negative temp id -- their create POST hasn't
  // resolved yet, see useTripwires.create) refuse ANY drag: there is no
  // server id yet for the *DragEnd callbacks' PUT to target. Blocking it
  // here -- before any listener is even attached -- is the simplest of the
  // two options the spec allows (block vs. queue): the operator simply
  // cannot start dragging a line that isn't confirmed yet, no visual
  // half-drag to unwind. useTripwires.update/remove hold the same guard as
  // a second line of defense.
  const startEndpointDrag = (tripwire, index) => (e) => {
    if (isProvisionalTripwireId(tripwire.id)) return;
    e.stopPropagation();
    e.preventDefault();
    onDragActiveChange?.(true);
    const svg = e.currentTarget.ownerSVGElement;
    let point = tripwire.points[index];

    const onMove = (ev) => {
      const rect = svg.getBoundingClientRect();
      const m = viewport.unproject({
        x: ev.clientX - rect.left,
        y: ev.clientY - rect.top,
      });
      const mx = Math.min(Math.max(m.x, 0), dimensions.width);
      const my = Math.min(Math.max(m.y, 0), dimensions.height);
      point = [Math.round(mx * 100) / 100, Math.round(my * 100) / 100];
      setDrag({ id: tripwire.id, index, point });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      activeDragListenersRef.current = null;
      // Commit the drop BEFORE clearing the local override: onEndpointDragEnd
      // triggers an optimistic parent-state update with this exact `point`,
      // so by the time `setDrag(null)` removes the local override the parent
      // already reflects the same position -- no one-frame revert-then-jump.
      onEndpointDragEnd(tripwire.id, index, point);
      setDrag(null);
      onDragActiveChange?.(false);
    };
    activeDragListenersRef.current = { onMove, onUp };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const startLaneDrag = (tripwire) => (e) => {
    if (isProvisionalTripwireId(tripwire.id)) return;
    e.stopPropagation();
    e.preventDefault();
    onDragActiveChange?.(true);
    const svg = e.currentTarget.ownerSVGElement;
    let xMeters = tripwire.points[0][0];

    const onMove = (ev) => {
      const rect = svg.getBoundingClientRect();
      const px = Math.min(
        Math.max(ev.clientX - rect.left, 0),
        canvasSize.width,
      );
      xMeters = Math.round(viewport.unproject({ x: px, y: 0 }).x * 100) / 100;
      setDrag({ id: tripwire.id, index: 0, point: [xMeters, 0] });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      activeDragListenersRef.current = null;
      // See startEndpointDrag's comment: commit before clearing the local
      // override, so there is no frame where the line falls back to its
      // pre-drag position.
      onLaneDragEnd(tripwire.id, xMeters);
      setDrag(null);
      onDragActiveChange?.(false);
    };
    activeDragListenersRef.current = { onMove, onUp };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Level 0: pointer-down on the line BODY. Below BODY_DRAG_THRESHOLD_PX of
  // total movement it is a click -> focus (level 1). Past the threshold it
  // is a drag -> translate both endpoints (plane) / slide the gate along the
  // lane axis (line/tunnel), clamped to the floor rect, PUT once on drop.
  // onDragActiveChange fires on the pointerDOWN itself (before click-vs-drag
  // is known) so the popover hides for the duration of EITHER outcome, per
  // the addendum -- a plain click still reopens it immediately on mouseup
  // since onSelect/the focus re-render happens right after.
  const startBodyDrag = (tripwire) => (e) => {
    if (isProvisionalTripwireId(tripwire.id)) return;
    e.stopPropagation();
    e.preventDefault();
    onDragActiveChange?.(true);
    const startClientX = e.clientX;
    const startClientY = e.clientY;
    let moved = false;
    let nextPoints = tripwire.points;

    const onMove = (ev) => {
      const dxPx = ev.clientX - startClientX;
      const dyPx = ev.clientY - startClientY;
      if (!moved && Math.hypot(dxPx, dyPx) < BODY_DRAG_THRESHOLD_PX) return;
      moved = true;

      if (layoutType === "plane") {
        const d = viewport.unprojectDelta({ x: dxPx, y: dyPx });
        nextPoints = translateSegment(
          tripwire.points,
          d.x,
          d.y,
          dimensions.width,
          dimensions.height,
        );
      } else {
        const dxMeters = viewport.unprojectDelta({ x: dxPx, y: 0 }).x;
        const newX = Math.min(
          Math.max(tripwire.points[0][0] + dxMeters, 0),
          dimensions.width || 0,
        );
        nextPoints = buildLaneSegment(Math.round(newX * 100) / 100, dimensions);
      }
      setBodyDrag({ id: tripwire.id, points: nextPoints });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      activeDragListenersRef.current = null;
      if (moved) {
        // Commit before clearing the local override (see startEndpointDrag's
        // comment) -- onBodyDragEnd triggers an optimistic parent-state
        // update with this exact `nextPoints`, so the line never visibly
        // snaps back to its pre-drag spot for a frame.
        onBodyDragEnd(tripwire.id, nextPoints);
        setBodyDrag(null);
      } else {
        setBodyDrag(null);
        onSelect(tripwire.id);
      }
      onDragActiveChange?.(false);
    };
    activeDragListenersRef.current = { onMove, onUp };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const effectivePoints = (tripwire) => {
    if (bodyDrag && bodyDrag.id === tripwire.id) return bodyDrag.points;
    if (drag && drag.id === tripwire.id) {
      if (layoutType === "plane") {
        return tripwire.points.map((p, i) =>
          i === drag.index ? drag.point : p,
        );
      }
      return buildLaneSegment(drag.point[0], dimensions);
    }
    return tripwire.points;
  };

  // Plane add mode: first pending click and the rubber-band end, in pixels.
  const pendingPx =
    layoutType === "plane" && drawingPoints?.length === 1
      ? viewport.projectPoints(drawingPoints)[0]
      : null;
  const previewPx = previewPoint ? viewport.projectPoints([previewPoint])[0] : null;

  return (
    <svg
      data-layer="tripwires"
      width={canvasSize.width}
      height={canvasSize.height}
      style={svgStyle}
    >
      {focusedId != null && onBackgroundClick && (
        <rect
          x={0}
          y={0}
          width={canvasSize.width}
          height={canvasSize.height}
          fill="transparent"
          style={{ pointerEvents: "auto" }}
          onClick={onBackgroundClick}
        />
      )}
      {tripwires.map((tw) => {
        const points = effectivePoints(tw);
        const isFocused = tw.id === focusedId;
        const color = getTripwireColor(tw.enabled);

        const [[x1, y1], [x2, y2]] = viewport.projectPoints(points);

        // Shaded restricted side(s) (2026-09-22(c)): computed in metres and
        // projected, drawn UNDER the line, no A/B labels or arrows any more.
        // Slightly lighter when this line is not the focused one (level 1).
        const shading = projectShading(
          viewport,
          points[0],
          points[1],
          tw.direction,
          SHADING_DEPTH_PX,
        );
        const focusOpacityFactor = isFocused ? 1 : 0.7;

        return (
          <g key={tw.id} style={{ pointerEvents: "auto" }}>
            {shading.length > 0 && (
              <>
                <defs>
                  {shading.map((s) => (
                    <linearGradient
                      key={`grad-${s.side}`}
                      id={`tripwire-shade-${layoutType}-${tw.id}-${s.side}`}
                      gradientUnits="userSpaceOnUse"
                      x1={s.gradient.x1}
                      y1={s.gradient.y1}
                      x2={s.gradient.x2}
                      y2={s.gradient.y2}
                    >
                      <stop
                        offset="0%"
                        stopColor={color}
                        stopOpacity={s.opacityFrom * focusOpacityFactor}
                      />
                      <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                {shading.map((s) => (
                  <polygon
                    key={`shade-${s.side}`}
                    points={s.polygon.map((p) => p.join(",")).join(" ")}
                    fill={`url(#tripwire-shade-${layoutType}-${tw.id}-${s.side})`}
                    pointerEvents="none"
                  />
                ))}
              </>
            )}
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={color}
              strokeWidth={isFocused ? 3 : 2}
              style={{ cursor: editorCursor(cursorMode, "tripwire") }}
              onMouseDown={editable ? startBodyDrag(tw) : undefined}
            />
            {editable && isFocused && layoutType === "plane" && (
              <>
                <circle
                  cx={x1}
                  cy={y1}
                  r={TRIPWIRE_HANDLE_RADIUS_PX}
                  fill={HANDLE_FILL}
                  stroke={color}
                  strokeWidth={1.5}
                  style={{ cursor: editorCursor(cursorMode, "tripwireEnd") }}
                  onMouseDown={startEndpointDrag(tw, 0)}
                />
                <circle
                  cx={x2}
                  cy={y2}
                  r={TRIPWIRE_HANDLE_RADIUS_PX}
                  fill={HANDLE_FILL}
                  stroke={color}
                  strokeWidth={1.5}
                  style={{ cursor: editorCursor(cursorMode, "tripwireEnd") }}
                  onMouseDown={startEndpointDrag(tw, 1)}
                />
              </>
            )}
            {editable && isFocused && layoutType !== "plane" && (
              <circle
                cx={x1}
                cy={canvasSize.height / 2}
                r={TRIPWIRE_HANDLE_RADIUS_PX}
                fill={HANDLE_FILL}
                stroke={color}
                strokeWidth={1.5}
                style={{ cursor: editorCursor(cursorMode, "lane") }}
                onMouseDown={startLaneDrag(tw)}
              />
            )}
          </g>
        );
      })}

      {pendingPx && (
        <>
          <circle
            cx={pendingPx[0]}
            cy={pendingPx[1]}
            r={4}
            fill={getTripwireColor(true)}
          />
          {previewPx && (
            <line
              x1={pendingPx[0]}
              y1={pendingPx[1]}
              x2={previewPx[0]}
              y2={previewPx[1]}
              stroke={getTripwireColor(true)}
              strokeWidth={2}
              strokeDasharray="4 4"
              pointerEvents="none"
            />
          )}
        </>
      )}
    </svg>
  );
};

export default TripwireOverlay;
